import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { Table, Button, Icon } from "semantic-ui-react";
import { toast, ToastContainer } from "react-toastify";
import { LoadingSpinner, BasicModal, ConfirmDialog } from "../../../Shared";
import { Country } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { CountryForm } from "../CountryForm";
import searchSvg from "../../../../assets/svg/search.svg";
import "./ListCountries.scss";

const countryController = new Country();

export function ListCountries({ countriesActive, reload, onReload }) {
  const { accessToken } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [countries,       setCountries]       = useState(null);
  const [countriesFilter, setCountriesFilter] = useState([]);
  const [search,          setSearch]          = useState("");
  const [showModal,       setShowModal]       = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [showConfirm,     setShowConfirm]     = useState(false);
  const [confirmMsg,      setConfirmMsg]      = useState("");
  const [isDelete,        setIsDelete]        = useState(false);
  const [rowMenu,         setRowMenu]         = useState(null); // { id, top, left, openUp } | null

  useEffect(() => {
    if (!rowMenu) return;
    const handler = (e) => {
      if (!e.target.closest(".countries-list__row-menu, .countries-list__row-menu-list")) setRowMenu(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [rowMenu]);

  const toggleRowMenu = (id, e) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 150;
    const openUp = rect.bottom + menuHeight > window.innerHeight;
    setRowMenu({
      id,
      left: rect.right - 170,
      top: openUp ? rect.top - 4 : rect.bottom + 4,
      openUp,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        setCountries(null);
        const res = await countryController.getCountries(accessToken, countriesActive);
        setCountries(res);
        setCountriesFilter(res);
      } catch {
        setCountries([]);
        setCountriesFilter([]);
      }
    })();
  }, [countriesActive, reload]);

  useEffect(() => {
    if (!countries) return;
    if (!search) { setCountriesFilter(countries); return; }
    const q = search.toLowerCase();
    setCountriesFilter(
      countries.filter((c) =>
        c.code?.toLowerCase().includes(q) || c.name?.toLowerCase().includes(q)
      )
    );
  }, [search, countries]);

  const openCreate = () => { setSelectedCountry(null); setShowModal(true); };
  const openEdit   = (c)  => { setSelectedCountry(c);   setShowModal(true); };

  const openToggle = (c) => {
    setSelectedCountry(c);
    setIsDelete(false);
    setConfirmMsg(`¿${c.active ? t("deactivate") : t("activate")} "${c.name}"?`);
    setShowConfirm(true);
  };

  const openDelete = (c) => {
    setSelectedCountry(c);
    setIsDelete(true);
    setConfirmMsg(`¿${t("delete")} "${c.name}"?`);
    setShowConfirm(true);
  };

  const handleToggle = async () => {
    try {
      await countryController.updateCountry(accessToken, selectedCountry._id, {
        active: !selectedCountry.active,
      });
      toast.success(selectedCountry.active ? t("country_deactivated") : t("country_activated"));
      setShowConfirm(false);
      onReload();
    } catch { toast.error(t("error_updating")); }
  };

  const handleDelete = async () => {
    try {
      await countryController.deleteCountry(accessToken, selectedCountry._id);
      toast.success(t("country_deleted"));
      setShowConfirm(false);
      onReload();
    } catch (err) { toast.error(err?.msg || t("error_deleting")); }
  };

  if (countries === null) return <LoadingSpinner />;

  return (
    <div className="countries-list">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="countries-list__card">
        <div className="countries-list__filters">
          <div className="search-user-wrapper">
            <img src={searchSvg} alt="search" className="search-user-icon" />
            <input
              className="search-user-input"
              placeholder={`${t("search")}...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button icon className="add-country-btn" title={t("create")} onClick={openCreate}>
            <Icon name="plus" />
          </Button>
        </div>

        {countriesFilter.length === 0 ? (
          <div className="list-empty-state">
                <Icon name="folder open outline" className="list-empty-state__icon" />
                <p className="list-empty-state__text">{t("no_results")}</p>
              </div>
        ) : (
          <div className="countries-list__tbl-scroll">
            <Table className="countries-table unstackable rtable-cards row-hover">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>{t("code")}</Table.HeaderCell>
                  <Table.HeaderCell>{t("name")}</Table.HeaderCell>
                  <Table.HeaderCell textAlign="right"></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {countriesFilter.map((c) => (
                  <Table.Row key={c._id}>
                    <Table.Cell data-label={t("code")}>{c.code}</Table.Cell>
                    <Table.Cell data-label={t("name")}>{c.name}</Table.Cell>
                    <Table.Cell textAlign="center" data-label="" className="countries-table__actions" onClick={(e) => e.stopPropagation()}>
                      <div className="countries-list__row-menu">
                        <button type="button" className="countries-list__row-menu-btn" onClick={(e) => toggleRowMenu(c._id, e)} aria-label={t("actions")}>
                          <Icon name="ellipsis vertical" />
                        </button>
                      </div>
                      {rowMenu?.id === c._id && ReactDOM.createPortal(
                        <div
                          className="countries-list__row-menu-list"
                          style={{
                            position: "fixed",
                            top: rowMenu.top,
                            left: rowMenu.left,
                            transform: rowMenu.openUp ? "translateY(-100%)" : "none",
                          }}
                        >
                          <button onClick={() => { openEdit(c); setRowMenu(null); }}>
                            <Icon name="pencil" />{t("edit")}
                          </button>
                          <button onClick={() => { openToggle(c); setRowMenu(null); }}>
                            <Icon name={c.active ? "ban" : "check"} />{c.active ? t("deactivate") : t("activate")}
                          </button>
                          <button className="countries-list__row-menu-item--danger" onClick={() => { openDelete(c); setRowMenu(null); }}>
                            <Icon name="trash" />{t("delete")}
                          </button>
                        </div>,
                        document.body
                      )}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </div>

      <BasicModal
        show={showModal}
        close={() => setShowModal(false)}
        title={selectedCountry ? `${t("edit")}: ${selectedCountry.name}` : t("new_country")}
        size="small"
        className="catalog-modal"
      >
        <CountryForm
          country={selectedCountry}
          close={() => setShowModal(false)}
          onReload={onReload}
        />
      </BasicModal>

      <ConfirmDialog
        open={showConfirm}
        title={isDelete ? "Eliminar" : (selectedCountry?.active ? "Desactivar" : "Activar")}
        message={confirmMsg}
        onCancel={() => setShowConfirm(false)}
        onConfirm={isDelete ? handleDelete : handleToggle}
        confirmLabel="Aceptar"
        cancelLabel={t("cancel")}
        confirmDanger={isDelete}
      />
    </div>
  );
}
